import type { FieldDef } from "@/lib/api/types";
import { widgetFor } from "@/lib/fieldTypes";
import type { FormValue } from "@/lib/form";

import styles from "./FieldSetForm.module.css";

type Props = {
  fields: FieldDef[];
  values: Record<string, FormValue>;
  onChange: (key: string, value: FormValue) => void;
  idPrefix?: string;
};

/** Renders one input per FieldDef, dispatching on the catalog type. Controlled:
 * the parent owns `values`. Shared by data entry (and, later, the builder). */
export function FieldSetForm({ fields, values, onChange, idPrefix = "field" }: Props) {
  return (
    <div className={styles.grid}>
      {fields.map((field) => (
        <FieldInput
          key={field.key}
          field={field}
          id={`${idPrefix}-${field.key}`}
          value={values[field.key] ?? (field.type === "boolean" ? false : "")}
          onChange={(v) => onChange(field.key, v)}
        />
      ))}
    </div>
  );
}

function FieldInput({
  field,
  id,
  value,
  onChange,
}: {
  field: FieldDef;
  id: string;
  value: FormValue;
  onChange: (value: FormValue) => void;
}) {
  const label = field.label ?? field.key;
  const widget = widgetFor(field.type);

  if (widget === "checkbox") {
    return (
      <div className={styles.checkbox}>
        <input
          id={id}
          type="checkbox"
          checked={value === true}
          onChange={(e) => onChange(e.target.checked)}
        />
        <label htmlFor={id} className={styles.label}>
          {label}
        </label>
      </div>
    );
  }

  return (
    <div className={styles.field}>
      <label htmlFor={id} className={styles.label}>
        {label}
        {field.required ? <span className={styles.required}>*</span> : null}
      </label>
      {widget === "select" ? (
        <select
          id={id}
          className={styles.select}
          value={String(value)}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">Select…</option>
          {(field.options ?? []).map((opt) => (
            <option key={String(opt.value)} value={String(opt.value)}>
              {opt.label ?? String(opt.value)}
            </option>
          ))}
        </select>
      ) : widget === "textarea" ? (
        <textarea
          id={id}
          className={styles.input}
          value={String(value)}
          rows={3}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <input
          id={id}
          className={styles.input}
          type={widget === "number" ? "number" : "text"}
          value={String(value)}
          step={field.step ?? (field.type === "integer" ? 1 : undefined)}
          min={field.min}
          max={field.max}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
      {field.unit ? <span className={styles.help}>Unit: {field.unit}</span> : null}
    </div>
  );
}
